import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { authProbeRetryDelay, retryAuthProbe } from "@/api/connectivity";
import { keys } from "@/api/queryKeys";
import type { Me } from "@/api/types";

/** The signed-in user. A 401 is final (signed out); a restarting backend
 *  (5xx, no response) is retried with backoff so it never reads as a
 *  sign-out — see ``authGate`` for what pages do with the result. */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => (await api.get<Me>("/auth/me")).data,
    staleTime: 60_000,
    retry: retryAuthProbe,
    retryDelay: authProbeRetryDelay,
  });
}

export type Preferences = { ui_language: string | null };

/** Mutation key of every account-preferences write, so the UI-language
 *  sync can tell that a save is still in flight. */
export const PREFERENCES_MUTATION_KEY = ["me", "preferences"] as const;

/** Store account preferences (``PATCH /auth/me/preferences``). Only the
 *  fields sent change; ``ui_language: null`` clears the choice. The
 *  server normalises the code (``sv-SE`` → ``sv``) and refuses one the
 *  UI can't show (422 ``ui_language_unsupported``). */
export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: PREFERENCES_MUTATION_KEY,
    mutationFn: async (payload: Partial<Preferences>) =>
      (await api.patch<Preferences>("/auth/me/preferences", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // End the chat and remove Intercom's cookies before the session goes
      // (facts §1.2: shutdown on logout). The module is tiny; the SDK
      // itself is only ever loaded by openChat().
      const { shutdownIntercom } = await import("@/features/support/useIntercomMessenger");
      shutdownIntercom();
      await api.post("/auth/logout");
    },
    onSuccess: () => {
      qc.clear();
      window.location.assign("/login");
    },
  });
}

/** Self-service account removal (Privacy Policy 10.1): anonymises the
 *  account, deletes the person's data and signs them out; a later sign-in
 *  creates a new account. The backend 409s ``{code: "sole_admin", orgs}``
 *  while the user is the only admin of an org; the caller explains it. */
export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete("/auth/me");
      // The account is gone: end the chat and remove Intercom's cookies
      // before leaving (spec §11.5), as logout does.
      const { shutdownIntercom } = await import("@/features/support/useIntercomMessenger");
      shutdownIntercom();
    },
    onSuccess: () => {
      qc.clear();
      window.location.assign("/login");
    },
  });
}
